/**
 * RequireMfa — gate de MFA obrigatório (feat: TOTP no login).
 *
 * Encaixado dentro de <RequireAuth> e de <RequireSenhaDefinida> (que roda antes),
 * por fora de <AppShell> (routes/index.tsx): nenhuma página é alcançável enquanto
 * o gate não resolver.
 *
 * Bootstrap/F5: a sessão É re-hidratada no boot (auth-context.tsx re-emite o
 * access token pelo cookie via /bff/auth/refresh). Mas o client supabase-js
 * NÃO tem sessão nesse momento (persistSession=false por design), então
 * listFactors()/challengeAndVerify falhariam. Por isso o gate checa primeiro
 * o claim `aal` do JWT re-hidratado: se já é aal2, MFA está satisfeito e
 * libera SEM tocar no supabase-js (o backend revalida aal2 a cada request).
 * O caminho enroll/challenge (aal1) só é exercitado logo após o login, quando
 * o client supabase-js ainda tem sessão.
 *
 *   - Sem fator TOTP verificado           → MfaEnrollGate (cadastro obrigatório)
 *   - Fator verificado mas sessão em aal1 → MfaChallengeGate (código obrigatório)
 *   - aal2                                → libera o app (<Outlet/>)
 *   - dev-login (sem Supabase real)       → libera direto — mesmo comportamento
 *     já documentado no MfaConfirmDialog (não há como fazer MFA sem sessão real).
 *
 * Erro ao listar fatores NÃO libera o acesso (mesma postura do step-up
 * reativo em mfa-confirm-dialog.tsx) — mostra erro com retry.
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { EVENTO_AUTENTICADOR_OBRIGATORIO } from '@/lib/api-client'
import { getSupabaseClient } from '@/lib/supabase-client'
import { getTokenPayload } from '@/store/token-store'
import { useAuth } from '@/features/auth/auth-context'

// Os portões de cadastro e de código só aparecem com o MFA pendente: carregam à
// parte para não pesar no chunk inicial de quem já entra em aal2.
const MfaEnrollGate = lazy(() => import('@/features/auth/mfa-enroll-gate').then((m) => ({ default: m.MfaEnrollGate })))
const MfaChallengeGate = lazy(() => import('@/features/auth/mfa-challenge-gate').then((m) => ({ default: m.MfaChallengeGate })))

type GateState =
  | { kind: 'checking' }
  | { kind: 'error' }
  | { kind: 'need-enroll' }
  | { kind: 'need-challenge'; factorId: string }
  | { kind: 'ok' }

function VerificandoMfa() {
  return (
    <div
      className="flex min-h-screen items-center justify-center"
      role="status"
      aria-label="Verificando autenticação multifator..."
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
    </div>
  )
}

export function RequireMfa() {
  const { isDevLogin, logout } = useAuth()
  const [state, setState] = useState<GateState>({ kind: 'checking' })

  const avaliar = useCallback(async () => {
    if (isDevLogin) {
      setState({ kind: 'ok' })
      return
    }
    setState({ kind: 'checking' })

    // Sessão já elevada (aal2): MFA satisfeito. Libera direto pelo claim, sem
    // depender do supabase-js — essencial no F5, quando o client não tem
    // sessão mas o token-store/cookie foram re-hidratados em aal2.
    if (getTokenPayload()?.aal === 'aal2') {
      setState({ kind: 'ok' })
      return
    }

    // aal1: precisa enrollar/desafiar via supabase-js (fluxo logo após o
    // login, quando o client ainda tem sessão viva).
    try {
      const { data, error } = await getSupabaseClient().auth.mfa.listFactors()
      if (error) {
        setState({ kind: 'error' })
        return
      }
      const verificado = data?.totp.find((f) => f.status === 'verified')
      if (!verificado) {
        setState({ kind: 'need-enroll' })
        return
      }
      setState({ kind: 'need-challenge', factorId: verificado.id })
    } catch {
      setState({ kind: 'error' })
    }
  }, [isDevLogin])

  useEffect(() => {
    void avaliar()
  }, [avaliar])

  useEffect(() => {
    if (isDevLogin) return
    const aoRecusar = () =>
      setState((atual) => (atual.kind === 'need-enroll' || atual.kind === 'need-challenge' ? atual : { kind: 'need-enroll' }))
    window.addEventListener(EVENTO_AUTENTICADOR_OBRIGATORIO, aoRecusar)
    return () => window.removeEventListener(EVENTO_AUTENTICADOR_OBRIGATORIO, aoRecusar)
  }, [isDevLogin])

  if (state.kind === 'checking') {
    return <VerificandoMfa />
  }

  if (state.kind === 'error') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-4 dark:bg-gray-950">
        <div className="w-full max-w-md space-y-4 rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div role="alert" className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            Não foi possível verificar seus fatores de autenticação. Tente novamente.
          </div>
          <div className="flex justify-center gap-2">
            <Button type="button" onClick={() => void avaliar()}>Tentar novamente</Button>
            <Button type="button" variant="ghost" onClick={() => { void logout() }}>Sair</Button>
          </div>
        </div>
      </div>
    )
  }

  if (state.kind === 'need-enroll') {
    return (
      <Suspense fallback={<VerificandoMfa />}>
        <MfaEnrollGate onDone={() => void avaliar()} />
      </Suspense>
    )
  }

  if (state.kind === 'need-challenge') {
    return (
      <Suspense fallback={<VerificandoMfa />}>
        <MfaChallengeGate factorId={state.factorId} onDone={() => void avaliar()} />
      </Suspense>
    )
  }

  return <Outlet />
}
